import type { AnyAction, ThunkDispatch } from '@reduxjs/toolkit';
import { useCallback, useMemo } from 'react';
import { useDispatch, useStore } from 'react-redux';

import {
  captureSidekickActingOrganizationScope,
  type SidekickActingOrganizationScope,
} from '../api/actingOrganization';
import type { Message as SavedMessage } from '../api/generated-api';
import { sidekickRtkClient } from '../api/overrides';
import { parseWireArtifacts } from '../sse/wireArtifacts';
import type { ArtifactRef, StateWithSidekickChat } from '../state/chatTypes';
import {
  findSavedAnswer,
  findSavedLatestQuestion,
} from '../utils/saved-conversation';

const PAGE_SIZE = 100;
const MAX_PAGES = 10;

/** Where a saved table or chart downloads from. */
export type ArtifactLocation = {
  conversationId: string;
  artifactId: string;
  /**
   * The organization the download was clicked in, so switching while the
   * artifact is looked up can't send it for another one.
   */
  organizationScope?: SidekickActingOrganizationScope;
};

/**
 * Finds the saved table or chart an answer showed.
 * @param answerId - The local id of the answer.
 * @param kind - Table or chart.
 * @param index - Which one of that kind, in the order the answer showed them.
 */
export type FindArtifact = (
  answerId: string,
  kind: ArtifactRef['kind'],
  index: number
) => Promise<ArtifactLocation | undefined>;

/** Lookups against the saved copy of the active conversation. */
export type SavedMessageLookup = {
  /**
   * Finds the saved id of the latest question, so an edit can name it.
   * Resolves `undefined` when it cannot be found.
   */
  findSavedQuestionId: (questionId: string) => Promise<string | undefined>;
  /** Resolves `undefined` when the artifact cannot be found. */
  findArtifact: FindArtifact;
};

/**
 * Looks up the ids the backend saved for local messages. Ids the answer
 * already carries are used as they are. Otherwise the saved conversation is
 * read, which also covers a turn whose answer never carried them. A failed
 * read resolves `undefined`, which keeps the caller on its fallback path.
 * @returns Stable lookup callbacks.
 */
export const useSavedMessageLookup = (): SavedMessageLookup => {
  const store = useStore<StateWithSidekickChat>();
  const dispatch = useDispatch<ThunkDispatch<unknown, unknown, AnyAction>>();

  const readSavedMessages = useCallback(
    async (
      conversationId: string,
      organizationScope: SidekickActingOrganizationScope
    ): Promise<SavedMessage[]> => {
      const saved: SavedMessage[] = [];
      let pageToken: string | undefined;
      for (let page = 0; page < MAX_PAGES; page += 1) {
        // Read once without staying subscribed, so later conversation
        // mutations don't refetch it in the background.
        const query = dispatch(
          sidekickRtkClient.endpoints.conversationServiceListScopedConversationMessages.initiate(
            {
              conversationId,
              pageSize: PAGE_SIZE,
              pageToken,
              organizationScope,
            },
            { forceRefetch: true }
          )
        );
        const response = await query.unwrap().finally(() => {
          query.unsubscribe();
        });
        saved.push(...(response.messages ?? []));
        pageToken = response.pagination?.nextPageToken;
        if (pageToken === undefined || pageToken === '') {
          break;
        }
      }
      // Matching needs the whole conversation, so a read cut off at the page
      // limit fails and the caller falls back.
      if (pageToken !== undefined && pageToken !== '') {
        throw new Error('Saved conversation is longer than the lookup reads');
      }
      return saved;
    },
    [dispatch]
  );

  const findSavedQuestionId = useCallback(
    async (questionId: string): Promise<string | undefined> => {
      const organizationScope = captureSidekickActingOrganizationScope();
      const { conversationId, messages } = store.getState().aiSidekickChat;
      const questionIndex = messages.findIndex(({ id }) => id === questionId);
      if (conversationId === null || questionIndex === -1) {
        return undefined;
      }
      // The question's own answer: the one stamped with it, or else the next
      // answer when that one isn't stamped for another question.
      const nextAnswer = messages
        .slice(questionIndex + 1)
        .find(({ role }) => role === 'assistant');
      const answer =
        messages.find(
          ({ role, sourceQuestionId }) =>
            role === 'assistant' && sourceQuestionId === questionId
        ) ??
        (nextAnswer?.sourceQuestionId === undefined ? nextAnswer : undefined);
      if (answer?.parentId !== undefined) {
        return answer.parentId;
      }
      try {
        const saved = await readSavedMessages(
          conversationId,
          organizationScope
        );
        return findSavedLatestQuestion(
          saved,
          messages,
          messages[questionIndex].content
        )?.id;
      } catch {
        return undefined;
      }
    },
    [readSavedMessages, store]
  );

  const findArtifact = useCallback<FindArtifact>(
    async (answerId, kind, index) => {
      const organizationScope = captureSidekickActingOrganizationScope();
      const { conversationId, messages } = store.getState().aiSidekickChat;
      if (conversationId === null) {
        return undefined;
      }
      const ofKind = (artifacts: ReadonlyArray<ArtifactRef> | undefined) =>
        artifacts?.filter((artifact) => artifact.kind === kind)[index]?.id;
      const knownId = ofKind(
        messages.find(({ id }) => id === answerId)?.artifacts
      );
      if (knownId !== undefined) {
        return { conversationId, artifactId: knownId, organizationScope };
      }
      try {
        const saved = await readSavedMessages(
          conversationId,
          organizationScope
        );
        const artifactId = ofKind(
          parseWireArtifacts(
            findSavedAnswer(saved, messages, answerId)?.artifacts
          )
        );
        return artifactId === undefined
          ? undefined
          : { conversationId, artifactId, organizationScope };
      } catch {
        return undefined;
      }
    },
    [readSavedMessages, store]
  );

  return useMemo(
    () => ({ findSavedQuestionId, findArtifact }),
    [findArtifact, findSavedQuestionId]
  );
};
